"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Heart, Eye, ShoppingBasket, ArrowRight,
  Star, RefreshCw, Shield, ChevronDown,
  X, Minus, Plus, Check, ShoppingCart,
  Loader2, Lock,
} from "lucide-react";
import { addToLocalCart, localCartCount } from "@/lib/cartUtils";
import SizeChartModal from "@/lib/SizeChartModal";
import {
  API_BASE, getToken, apiFetch, resolveImg, FALLBACK,
  type Product, type DBCategory, type Variants,
  normaliseProduct, pickDefaultVariant, resolveSizeForColor,
} from "@/lib/productShared";

const ALL_FILTER = { id: null as number | null, name: "All", slug: null as string | null };

const INITIAL_COUNT   = 8;
const LOAD_MORE_COUNT = 4;

const LOOKBOOK = [
  { title: "Power Dressing",  subtitle: "Suits & Trousers",  img: "https://images.unsplash.com/photo-1551803091-e20673f15770?w=600&q=80", count: "19 Pieces" },
  { title: "Bridal Radiance", subtitle: "Lehengas & Gowns", img: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&q=80", count: "24 Pieces" },
  { title: "Everyday Grace",  subtitle: "Shirts & Blouses",  img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&q=80", count: "31 Pieces" },
];

const STYLE_TIPS = [
  { icon: "✦", title: "Layer Intentionally",  tip: "A sheer dupatta over a structured blazer adds dimension without volume. Drape loosely off one shoulder for an effortless finish." },
  { icon: "◆", title: "Mix Textures",          tip: "Pair matte linen trousers with a silk blouse. The contrast creates visual interest and elevates even casual looks." },
  { icon: "●", title: "Accessorise Minimally", tip: "With heavy embroidery or print, choose one statement piece and let the garment breathe." },
  { icon: "▲", title: "Embrace Proportion",    tip: "Wide-leg trousers with a fitted blouse. Full lehenga with a cropped blouse. Contrasting volumes create striking silhouettes." },
];

// Route helper — builds the product detail page URL.
function productHref(p: Product): string {
  return `/product/${p.slug || p.id}`;
}

// ─── Dots ─────────────────────────────────────────────────────────────────────
function StepDots({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`rounded-full transition-all duration-300 ${
          i < step    ? "w-4 h-1.5 bg-[#b8955a]"
          : i === step ? "w-2 h-2 bg-[#b8955a] opacity-50"
          : "w-1.5 h-1.5 bg-[#ddd]"
        }`} />
      ))}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[99999] flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl text-white text-[13px] font-['DM_Mono'] tracking-wide ${type === "success" ? "bg-emerald-600" : "bg-red-500"}`}>
      {type === "success" ? <Check size={15} /> : <X size={15} />}
      {message}
    </div>
  );
}

function CartBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="absolute -top-1.5 -right-1.5 bg-[#b8955a] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
      {count > 9 ? "9+" : count}
    </span>
  );
}

// ─── Quick Add Popup ──────────────────────────────────────────────────────────
function QuickAddPopup({
  product, onClose, onAddToCart, onViewFull,
}: {
  product:    Product;
  onClose:    () => void;
  onAddToCart:(productId: number, size: string, color: string, qty: number) => Promise<void>;
  onViewFull: () => void;
}) {
  const hasSizes  = (product.variants?.sizes  ?? []).length > 0;
  const hasColors = (product.variants?.colors ?? []).length > 0;

  const totalSteps = (hasColors ? 1 : 0) + (hasSizes ? 1 : 0) + 1;
  const colorStep  = hasColors ? 0 : -1;
  const sizeStep   = hasColors && hasSizes ? 1 : (!hasColors && hasSizes ? 0 : -1);
  const addStep    = totalSteps - 1;

  const [currentStep, setCurrentStep] = useState(addStep);
  const [selColor,    setSelColor]    = useState("");
  const [selSize,     setSelSize]     = useState("");
  const [qty,         setQty]         = useState(1);
  const [adding,      setAdding]      = useState(false);
  const [added,       setAdded]       = useState(false);
  const [imgFade,     setImgFade]     = useState(true);
  const [showSizeChart, setShowSizeChart] = useState(false);

  useEffect(() => {
    const defaults = pickDefaultVariant(product.variants);
    setSelColor(defaults.color);
    setSelSize(defaults.size);
    setCurrentStep(addStep);
    setQty(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const previewImg = useMemo(() => {
    const colorObj = product.variants?.colors?.find((c) => c.value === selColor);
    if (colorObj?.images?.length) {
      const sorted = [...colorObj.images].sort((a, b) => {
        if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      });
      return resolveImg(sorted[0].image_url);
    }
    const fallbackSrc = product.images?.length
      ? (product.images.find((m) => m.is_primary)?.src ?? product.images[0]?.src)
      : (product.media?.find((m) => m.is_primary)?.src ?? product.media?.[0]?.src);
    return resolveImg(fallbackSrc);
  }, [product, selColor]);

  useEffect(() => {
    setImgFade(false);
    const t = setTimeout(() => setImgFade(true), 60);
    return () => clearTimeout(t);
  }, [previewImg]);

  const availableStock: number = (() => {
    if (selSize && selColor) {
      const combo = product.variants?.items?.find((it) => it.size === selSize && it.color === selColor);
      if (combo !== undefined) return Number(combo.stock_qty) || 0;
    }
    if (selSize)  { const s = product.variants?.sizes.find((s)  => s.value === selSize);  if (s) return s.stock_qty; }
    if (selColor) { const c = product.variants?.colors.find((c) => c.value === selColor); if (c) return c.stock_qty; }
    return product.stock;
  })();

  const discount = product.sale_price
    ? Math.round((1 - Number(product.sale_price) / Number(product.regular_price)) * 100) : 0;

  const stepLabel = (step: number): string => {
    if (step === colorStep) return "Colour";
    if (step === sizeStep)  return "Size";
    return "Add to Cart";
  };

  const isStepComplete = (step: number): boolean => {
    if (step === colorStep) return selColor !== "";
    if (step === sizeStep)  return selSize  !== "";
    return false;
  };

  const handleColorSelect = (v: string) => {
    const nextColor = v === selColor ? "" : v;
    setSelColor(nextColor);
    if (nextColor) {
      setSelSize((prevSize) => resolveSizeForColor(product.variants, nextColor, prevSize));
    }
  };

  const handleSizeSelect = (v: string) => {
    setSelSize(v === selSize ? "" : v);
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      await onAddToCart(product.id, selSize, selColor, qty);
      setAdded(true);
      setTimeout(() => { setAdded(false); onClose(); }, 1600);
    } finally { setAdding(false); }
  };

  const isOnAddStep = currentStep === addStep;
  const canGoBack   = currentStep > 0;

  const isOutOfStock = (hasColors || hasSizes) && availableStock === 0;

  return (
    <>
      <div className="fixed inset-0 z-[9990]" onClick={onClose} />
      <div
        className="absolute bottom-0 left-0 right-0 z-[9991] bg-white border-t-2 border-[#b8955a] shadow-[0_-8px_40px_rgba(0,0,0,0.18)]"
        style={{ animation: "quickAddSlide 0.28s cubic-bezier(0.34,1.1,0.64,1) forwards" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-4 pt-3 pb-2 gap-3">
          <div className="w-14 h-16 rounded-lg overflow-hidden bg-[#f0ebe3] flex-shrink-0 border border-[#ece7df]">
            <img
              src={previewImg}
              alt={product.name}
              className="w-full h-full object-cover transition-opacity duration-200"
              style={{ opacity: imgFade ? 1 : 0 }}
              onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
            />
          </div>

          <div className="flex-1 min-w-0 pr-2">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="font-['DM_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#b8955a]">{product.category}</p>
              <StepDots step={currentStep} total={totalSteps} />
            </div>
            <p className="font-['Cormorant_Garamond'] italic text-[0.95rem] text-[#1a1a1a] leading-tight line-clamp-1">{product.name}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <div className="text-right">
              <span className="font-['DM_Mono'] text-[13px] font-medium text-[#1a1a1a]">
                ₹{(product.sale_price ?? product.regular_price).toLocaleString()}
              </span>
              {product.sale_price && (
                <div className="flex items-center gap-1 justify-end">
                  <span className="font-['DM_Mono'] text-[9px] text-[#bbb] line-through">₹{Number(product.regular_price).toLocaleString()}</span>
                  <span className="bg-red-100 text-red-600 text-[8px] font-bold px-1 rounded font-['DM_Mono']">-{discount}%</span>
                </div>
              )}
            </div>
            <button onClick={onClose} className="ml-1 w-6 h-6 rounded-full bg-[#f5f0e8] flex items-center justify-center hover:bg-[#ede7db] transition-colors">
              <X size={11} className="text-[#666]" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-0 mx-3 mb-2 bg-[#faf7f2] rounded-lg overflow-hidden border border-[#ede8e0]">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const done   = i < currentStep || isStepComplete(i);
            const active = i === currentStep;
            return (
              <button key={i} onClick={() => setCurrentStep(i)}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[9px] font-['DM_Mono'] tracking-[0.12em] uppercase transition-all duration-200 border-r last:border-r-0 border-[#ede8e0] ${
                  done && !active ? "bg-[#b8955a]/10 text-[#b8955a] cursor-pointer"
                  : active        ? "bg-[#b8955a] text-white font-bold"
                  : "text-[#ccc] cursor-not-allowed"
                }`}>
                {done && !active ? <Check size={8} strokeWidth={3} /> : <span className="w-3 h-3 rounded-full border border-current flex items-center justify-center text-[8px] font-bold">{i + 1}</span>}
                <span>{stepLabel(i)}</span>
              </button>
            );
          })}
        </div>

        <div className="px-3 pb-3 space-y-2.5">
          {currentStep === colorStep && hasColors && (
            <div className="space-y-2" style={{ animation: "stepSlideIn 0.22s ease-out forwards" }}>
              <div className="flex items-center justify-between">
                <p className="font-['DM_Mono'] text-[11px] tracking-[0.18em] uppercase text-[#1a1a1a] font-semibold flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-[#b8955a] text-white text-[8px] flex items-center justify-center font-bold">1</span>
                  Choose a Colour
                </p>
                {selColor && <p className="font-['DM_Mono'] text-[10px] text-[#b8955a] capitalize font-medium">{selColor} ✓</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {product.variants!.colors.map((c) => {
                  const oos = c.stock_qty === 0;
                  const sel = selColor === c.value;
                  return (
                    <button key={c.value} onClick={() => !oos && handleColorSelect(c.value)} disabled={oos}
                      className={`group/col relative flex flex-col items-center gap-1 transition-all duration-200 ${oos ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}>
                      <div className={`w-8 h-8 rounded-full transition-all duration-200 flex-shrink-0 ${
                        sel ? "ring-2 ring-offset-2 ring-[#1a1a1a] scale-110 shadow-md" : oos ? "ring-1 ring-[#eee]" : "ring-1 ring-[#ccc] hover:ring-[#888] hover:scale-105"
                      }`} style={{ backgroundColor: c.hex }}>
                        {sel && <span className="absolute inset-0 flex items-center justify-center"><Check size={12} className="text-white drop-shadow-sm" strokeWidth={3} /></span>}
                        {oos && <span className="absolute inset-0 flex items-center justify-center"><X size={10} className="text-white/60" /></span>}
                      </div>
                      <span className={`font-['DM_Mono'] text-[8px] capitalize leading-none ${sel ? "text-[#b8955a] font-bold" : "text-[#999]"}`}>{c.value}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {currentStep === sizeStep && hasSizes && (
            <div className="space-y-2" style={{ animation: "stepSlideIn 0.22s ease-out forwards" }}>
              <div className="flex items-center justify-between">
                <p className="font-['DM_Mono'] text-[11px] tracking-[0.18em] uppercase text-[#1a1a1a] font-semibold flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-[#b8955a] text-white text-[8px] flex items-center justify-center font-bold">{sizeStep + 1}</span>
                  Choose a Size
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSizeChart(true)}
                    className="font-['DM_Mono'] text-[9px] uppercase tracking-wide text-[#b8955a] underline underline-offset-2 hover:text-[#1a1a1a] transition-colors"
                  >
                    Size Chart
                  </button>
                  {selSize && <p className="font-['DM_Mono'] text-[10px] text-[#b8955a] font-medium">{selSize} — {product.variants!.sizes.find((s) => s.value === selSize)?.stock_qty ?? 0} in stock ✓</p>}
                </div>
              </div>
              {selColor && (
                <div className="flex items-center gap-1.5 bg-[#faf7f2] border border-[#ede8e0] rounded-lg px-2.5 py-1.5">
                  <div className="w-3.5 h-3.5 rounded-full ring-1 ring-[#ccc] flex-shrink-0" style={{ backgroundColor: product.variants?.colors.find((c) => c.value === selColor)?.hex ?? "#888" }} />
                  <span className="font-['DM_Mono'] text-[9px] text-[#888] capitalize">{selColor}</span>
                  <Check size={8} className="text-[#b8955a]" strokeWidth={3} />
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {product.variants!.sizes.map((s) => {
                  const combo = selColor
                    ? product.variants?.items?.find((it) => it.color === selColor && it.size === s.value)
                    : undefined;
                  const oos = combo !== undefined ? combo.stock_qty === 0 : s.stock_qty === 0;
                  const sel = selSize === s.value;
                  return (
                    <button key={s.value} onClick={() => !oos && handleSizeSelect(s.value)} disabled={oos}
                      className={`relative px-3 py-1.5 rounded-lg font-['DM_Mono'] text-[10px] border-2 transition-all duration-150 ${
                        sel ? "border-[#1a1a1a] bg-[#1a1a1a] text-white shadow"
                        : oos ? "border-[#eee] text-[#ccc] cursor-not-allowed line-through bg-[#fafafa]"
                        : "border-[#ddd] text-[#444] hover:border-[#b8955a] hover:text-[#b8955a]"
                      }`}>
                      {s.value}
                      {oos && <span className="absolute -top-1 -right-1 bg-red-400 text-white text-[7px] w-3 h-3 rounded-full flex items-center justify-center">✕</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isOnAddStep && (
            <div className="space-y-2" style={{ animation: "stepSlideIn 0.22s ease-out forwards" }}>
              <div className="flex items-center gap-2 flex-wrap">
                {selColor && (
                  <div className="flex items-center gap-1.5 bg-[#f5f0e8] border border-[#ede8e0] rounded-lg px-2.5 py-1.5">
                    <div className="w-3.5 h-3.5 rounded-full ring-1 ring-[#ccc] flex-shrink-0" style={{ backgroundColor: product.variants?.colors.find((c) => c.value === selColor)?.hex ?? "#888" }} />
                    <span className="font-['DM_Mono'] text-[9px] text-[#888] capitalize">{selColor}</span>
                    <button onClick={() => setCurrentStep(colorStep === -1 ? 0 : colorStep)} className="ml-0.5 text-[#ccc] hover:text-[#b8955a]" aria-label="Change colour"><X size={8} /></button>
                  </div>
                )}
                {selSize && (
                  <div className="flex items-center gap-1.5 bg-[#f5f0e8] border border-[#ede8e0] rounded-lg px-2.5 py-1.5">
                    <span className="font-['DM_Mono'] text-[9px] font-bold text-[#1a1a1a]">{selSize}</span>
                    <span className="font-['DM_Mono'] text-[9px] text-[#aaa]">·</span>
                    <span className="font-['DM_Mono'] text-[9px] text-[#888]">{availableStock} left</span>
                    <button onClick={() => setCurrentStep(sizeStep === -1 ? 0 : sizeStep)} className="ml-0.5 text-[#ccc] hover:text-[#b8955a]" aria-label="Change size"><X size={8} /></button>
                  </div>
                )}
              </div>
              {availableStock > 0 && availableStock <= 5 && <p className="font-['DM_Mono'] text-[9px] text-amber-600 flex items-center gap-1">Hurry  Only {availableStock} left!</p>}
              {isOutOfStock && <p className="font-['DM_Mono'] text-[9px] text-red-500 flex items-center gap-1">✕ Out of stock for this selection</p>}
              <div className="flex items-center justify-between">
                <p className="font-['DM_Mono'] text-[10px] tracking-[0.15em] uppercase text-[#888]">Qty</p>
                <div className="flex items-center gap-1 bg-[#f7f4ef] rounded-lg px-2 py-1">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-4 h-4 flex items-center justify-center text-[#555] hover:text-[#1a1a1a]"><Minus size={9} /></button>
                  <span className="font-['DM_Mono'] text-[12px] w-4 text-center text-[#1a1a1a]">{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(Math.max(availableStock, 1), q + 1))} className="w-4 h-4 flex items-center justify-center text-[#555] hover:text-[#1a1a1a]"><Plus size={9} /></button>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            {canGoBack && (
              <button onClick={() => setCurrentStep((s) => s - 1)}
                className="px-3 py-2.5 rounded-xl border border-[#e8e4dd] bg-[#faf7f2] hover:bg-[#f0ebe3] transition-colors text-[#666] font-['DM_Mono'] text-[10px] tracking-wide flex items-center gap-1">
                ← Back
              </button>
            )}
            {!isOnAddStep ? (
              <button
                onClick={() => setCurrentStep((s) => Math.min(s + 1, addStep))}
                className="flex-1 flex items-center justify-center gap-1.5 font-['DM_Mono'] text-[10px] tracking-[0.12em] uppercase py-2.5 rounded-xl transition-all duration-200 bg-[#1a1a1a] text-white hover:bg-[#333]">
                Next Step →
              </button>
            ) : (
              <>
                <button onClick={handleAdd} disabled={adding || added || isOutOfStock}
                  className={`flex-1 flex items-center justify-center gap-1.5 font-['DM_Mono'] text-[10px] tracking-[0.12em] uppercase py-2.5 rounded-xl transition-all duration-200 ${
                    added ? "bg-emerald-600 text-white"
                    : isOutOfStock ? "bg-[#f1efe9] text-[#b5b2a9] cursor-not-allowed"
                    : "bg-[#051173] hover:bg-[#C7AB5F] text-white"
                  }`}>
                  {adding ? <Loader2 size={11} className="animate-spin" /> : added ? <Check size={11} /> : <ShoppingBasket size={11} />}
                  {adding ? "Adding…" : added ? "Added!" : isOutOfStock ? "Out of Stock" : "Add to Cart"}
                </button>
                <button onClick={onViewFull} className="px-3 py-2.5 rounded-xl border border-[#e8e4dd] bg-[#faf7f2] hover:bg-[#f0ebe3] transition-colors flex items-center justify-center" title="View full details">
                  <Eye size={13} className="text-[#666]" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showSizeChart && (
        <SizeChartModal
          gender={product.gender ?? "women"}
          onClose={() => setShowSizeChart(false)}
        />
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WomenPage() {
  const router = useRouter();

  const [products,          setProducts]          = useState<Product[]>([]);
  const [dbCategories,      setDbCategories]      = useState<DBCategory[]>([]);
  const [loading,           setLoading]           = useState(true);
  const [catsLoading,       setCatsLoading]       = useState(true);
  const [error,             setError]             = useState("");
  const [activeCatId,       setActiveCatId]       = useState<number | null>(null);
  const [wishlist,          setWishlist]          = useState<number[]>([]);
  const [visibleCount,      setVisibleCount]      = useState(INITIAL_COUNT);
  const [showMoreDropdown,  setShowMoreDropdown]  = useState(false);
  const [showMobileFilter,  setShowMobileFilter]  = useState(false);
  const [quickAddProduct,   setQuickAddProduct]   = useState<Product | null>(null);
  const [cartCount,         setCartCount]         = useState(0);
  const [toast,             setToast]             = useState<{ message: string; type: "success" | "error" } | null>(null);
  const moreRef         = useRef<HTMLDivElement | null>(null);
  const mobileFilterRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCartCount(localCartCount());
  }, []);

  const fetchCategories = useCallback(async () => {
    setCatsLoading(true);
    try {
      const data = await apiFetch<{ success: boolean; data: DBCategory[] }>(
        "/categories?product_type=WOMEN&status=active&limit=1000"
      );
      if (data.success) setDbCategories(data.data);
    } catch { /* non-critical */ }
    finally { setCatsLoading(false); }
  }, []);

  const topLevelCats = useMemo(() => dbCategories.filter((c) => c.parent_id === null), [dbCategories]);
  const subCats      = useMemo(() => {
    if (activeCatId === null) return [];
    return dbCategories.filter((c) => c.parent_id === activeCatId);
  }, [dbCategories, activeCatId]);

  const INLINE_LIMIT = 5;
  const inlineCats   = topLevelCats.slice(0, INLINE_LIMIT);
  const overflowCats = topLevelCats.slice(INLINE_LIMIT);
  const isMoreActive = overflowCats.some((c) => c.id === activeCatId);

  const allMobileOptions = useMemo(() => [
    ALL_FILTER,
    ...topLevelCats.map((c) => ({ id: c.id, name: c.name, slug: c.slug })),
  ], [topLevelCats]);

  const fetchProducts = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ gender: "WOMEN", status: "active", limit: "1000" });
      if (activeCatId !== null) params.set("category_id", String(activeCatId));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data: any = await apiFetch(`/products?${params}`);
      if (!data.success) throw new Error(data.message);

      const items: Product[] = (data.data ?? []).map(normaliseProduct);
      setProducts(items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load products");
    } finally { setLoading(false); }
  }, [activeCatId]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((d) => { if (d.success) setWishlist(d.data.map((item: any) => item.productId)); })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchProducts(); setVisibleCount(INITIAL_COUNT); }, [fetchProducts]);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setShowMoreDropdown(false);
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(e.target as Node)) setShowMobileFilter(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setQuickAddProduct(null); setShowMobileFilter(false); }
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, []);

  const visibleProducts = products.slice(0, visibleCount);
  const hasMore         = visibleCount < products.length;

  const handleAddToCart = async (productId: number, size: string, color: string, qty: number) => {
    const product = products.find((p) => p.id === productId);
    const token = getToken();

    if (token) {
      try {
        const res = await fetch(`${API_BASE}/cart/add`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            product_id: productId,
            size:       size  || null,
            color:      color || null,
            quantity:   qty,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) throw new Error(json.message || "Failed to add to cart");
        setToast({ message: "Added to cart!", type: "success" });
      } catch {
        setToast({ message: "Could not add to cart. Try again.", type: "error" });
      }
      return;
    }

    const colorObj = product?.variants?.colors?.find((c) => c.value === color);
    const colorImg = colorObj?.images?.length ? resolveImg(colorObj.images[0].image_url) : null;
    const galleryImgs = (product?.images?.length ? product.images : product?.media)
      ?.map((m) => resolveImg(m.src)) ?? [];

    addToLocalCart({
      productId,
      title:      product?.name ?? `Product ${productId}`,
      sku:        product?.sku  ?? null,
      color:      color || null,
      size:       size  || null,
      price:      Number(product?.regular_price ?? 0),
      salePrice:  product?.sale_price ? Number(product.sale_price) : null,
      quantity:   qty,
      image:      colorImg ?? product?.media?.find((m) => m.is_primary)?.src ?? product?.media?.[0]?.src ?? null,
      colorImage: colorImg,
      selectedImage: colorImg,
      gallery:    galleryImgs,
      inStock:    (product?.stock ?? 0) > 0,
      stock:      product?.stock ?? 0,
      variantHex: colorObj?.hex ?? null,
      material:   product?.material ?? null,
      slug:       product?.slug ?? null,
      delivery:     "Standard delivery in 4–7 business days",
      returnPolicy: "14-day hassle-free returns",
      discountPercent: product?.sale_price
        ? Math.round((1 - Number(product.sale_price) / Number(product.regular_price)) * 100)
        : 0,
      rating:      product?.rating_avg   ?? null,
      reviewCount: product?.rating_count ?? null,
      badges: {
        isNew:        !!product?.is_new,
        isTrending:   !!product?.is_trending,
        isFeatured:   !!product?.is_featured,
        isBestSeller: !!product?.is_best_seller,
      },
    });
    setCartCount(localCartCount());
    setToast({ message: "Added to cart!", type: "success" });
  };

  const toggleWishlist = async (productId: number) => {
    const token = getToken();
    if (!token) { setToast({ message: "Please login to save to wishlist", type: "error" }); return; }
    const isWishlisted = wishlist.includes(productId);
    setWishlist((prev) => isWishlisted ? prev.filter((x) => x !== productId) : [...prev, productId]);
    try {
      if (isWishlisted) {
        await fetch(`${API_BASE}/wishlist/product/${productId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
        setToast({ message: "Removed from wishlist", type: "success" });
      } else {
        await fetch(`${API_BASE}/wishlist`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ product_id: productId }),
        });
        setToast({ message: "Saved to wishlist!", type: "success" });
      }
    } catch {
      setWishlist((prev) => isWishlisted ? [...prev, productId] : prev.filter((x) => x !== productId));
      setToast({ message: "Could not update wishlist", type: "error" });
    }
  };

  const primaryImg = (p: Product) =>
    p.media?.length ? resolveImg(p.media.find((m) => m.is_primary)?.src ?? p.media[0]?.src) : FALLBACK;

  const activeCatName = activeCatId === null
    ? "Women's Collection"
    : (dbCategories.find((c) => c.id === activeCatId)?.name ?? "Collection");

  const activeMobileLabel = activeCatId === null
    ? "All"
    : (dbCategories.find((c) => c.id === activeCatId)?.name ?? "Filter");


  const goToProduct = (p: Product) => router.push(productHref(p));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
        html { scroll-behavior: smooth; }
        @keyframes slideIn       { from { transform: translateY(8px); opacity:0 } to { transform:translateY(0); opacity:1 } }
        @keyframes quickAddSlide { from { opacity:0; transform:translateY(100%) } to { opacity:1; transform:translateY(0) } }
        @keyframes stepSlideIn   { from { opacity:0; transform:translateX(-6px) } to { opacity:1; transform:translateX(0) } }
        .more-dropdown    { animation: slideIn 0.22s ease-out forwards; }
        .hide-scrollbar::-webkit-scrollbar { display:none }
        .hide-scrollbar   { -ms-overflow-style:none; scrollbar-width:none }
      `}</style>

      <main className="bg-[#fdfbf8] text-[#1a1a1a] font-['Cormorant_Garamond',Georgia,serif] pt-16 md:pt-20 min-h-screen overflow-x-hidden">

        {/* Floating cart */}
        <div className="fixed top-100 right-4 z-[9998]">
          <Link href="/user/cart" className="relative w-11 h-11 bg-white border border-[#e8e4dd] rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow">
            <ShoppingCart size={18} className="text-[#1a1a1a]" />
            <CartBadge count={cartCount} />
          </Link>
        </div>

        {/* ── HERO ── */}
        <section className="relative h-[460px] md:h-[540px] flex items-center justify-center overflow-hidden pt-20">
          <img src="/hwomen.png" alt="Women's Collection" className="absolute inset-0 w-full h-full object-cover brightness-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/60" />
          <div className="relative z-10 text-center text-white px-6">
            <p className="font-['DM_Mono'] text-[13px] tracking-[0.3em] uppercase text-white/60 mb-4">Modish Kreations</p>
            <h1 className="font-['Cormorant_Garamond'] italic font-light text-[clamp(3rem,7vw,5.5rem)] leading-[1] text-white mb-4">
              Women's<br /><span className="text-[#c8a96e]">Collection</span>
            </h1>
            <div className="w-9 h-px bg-[#c8a96e] mx-auto mb-4" />
            <p className="font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase text-white/50">
              {loading ? "Loading…" : `${products.length} exclusive pieces`}
            </p>
          </div>
        </section>

        {/* ── FILTER BAR ── */}
        <div className="border-t border-b border-[#ede8e0] bg-[#faf8f5] mt-5 px-4 md:px-14 relative z-30">

          {/* MOBILE filter */}
          <div className="md:hidden py-3 flex items-center gap-3" ref={mobileFilterRef}>
            <span className="font-['DM_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#aaa] shrink-0">Filter:</span>
            <div className="relative flex-1">
              <button
                onClick={() => setShowMobileFilter((p) => !p)}
                className={`w-full flex items-center justify-between gap-2 font-['DM_Mono'] text-[12px] tracking-[0.15em] uppercase px-4 py-2.5 border transition-all duration-200
                [clip-path:polygon(4px_0%,calc(100%-4px)_0%,100%_4px,100%_calc(100%-4px),calc(100%-4px)_100%,4px_100%,0%_calc(100%-4px),0%_4px)]
                ${activeCatId === null ? "bg-[#051173] text-white border-[#051173]" : "bg-white text-[#051173] border-[#e0dbd2]"}`}
              >
                <span>{activeMobileLabel}</span>
                <ChevronDown size={13} className={`transition-transform duration-200 ${showMobileFilter ? "rotate-180" : ""}`} />
              </button>
              {showMobileFilter && (
                <div className="more-dropdown absolute left-0 right-0 top-full mt-1 bg-white border border-[#e8e4dd] shadow-2xl z-[999] max-h-[60vh] overflow-y-auto">
                  {catsLoading ? (
                    <div className="px-4 py-3 font-['DM_Mono'] text-[11px] text-[#b8955a] animate-pulse">Loading…</div>
                  ) : (
                    allMobileOptions.map((opt) => (
                      <button key={opt.id ?? "all"}
                        onClick={() => { setActiveCatId(opt.id); setVisibleCount(INITIAL_COUNT); setQuickAddProduct(null); setShowMobileFilter(false); }}
                        className={`flex items-center justify-between w-full text-left px-4 py-3 font-['DM_Mono'] text-[12px] tracking-[0.12em] uppercase border-b last:border-b-0 border-[#f0ebe3] transition-colors ${
                          activeCatId === opt.id ? "bg-[#051173] text-white" : "text-[#051173] hover:bg-[#f5f5f5]"
                        }`}>
                        <span>{opt.name}</span>
                        {activeCatId === opt.id && <Check size={12} strokeWidth={3} />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* DESKTOP filter */}
          <div className="hidden md:block">
            <div className="relative py-4 pr-[130px]">
              <div className="flex items-center gap-3 overflow-x-auto hide-scrollbar whitespace-nowrap">
                <button
                  onClick={() => { setActiveCatId(null); setVisibleCount(INITIAL_COUNT); setQuickAddProduct(null); }}
                  className={`shrink-0 font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase px-4 py-2 border transition-all duration-200
                  [clip-path:polygon(4px_0%,calc(100%-4px)_0%,100%_4px,100%_calc(100%-4px),calc(100%-4px)_100%,4px_100%,0%_calc(100%-4px),0%_4px)]
                  ${activeCatId === null ? "bg-[#051173] text-white border-[#051173]" : "bg-white text-[#051173] border-[#e0dbd2] hover:border-[#051173]"}`}
                >
                  All
                </button>

                {catsLoading ? (
                  <span className="font-['DM_Mono'] text-[11px] text-[#b8955a] tracking-wide animate-pulse">Loading categories…</span>
                ) : (
                  inlineCats.map((cat) => (
                    <button key={cat.id}
                      onClick={() => { setActiveCatId(cat.id); setVisibleCount(INITIAL_COUNT); setQuickAddProduct(null); }}
                      className={`shrink-0 font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase px-4 py-2 border transition-all duration-200
                      [clip-path:polygon(4px_0%,calc(100%-4px)_0%,100%_4px,100%_calc(100%-4px),calc(100%-4px)_100%,4px_100%,0%_calc(100%-4px),0%_4px)]
                      ${activeCatId === cat.id ? "bg-[#051173] text-white border-[#051173]" : "bg-white text-[#051173] border-[#e0dbd2] hover:border-[#051173]"}`}
                    >
                      {cat.name}
                    </button>
                  ))
                )}
              </div>

              {overflowCats.length > 0 && (
                <div ref={moreRef} className="absolute right-0 top-1/2 -translate-y-1/2 z-50">
                  <button onClick={() => setShowMoreDropdown((p) => !p)}
                    className={`font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase px-4 py-2 border transition-all duration-200 flex items-center gap-2
                    [clip-path:polygon(4px_0%,calc(100%-4px)_0%,100%_4px,100%_calc(100%-4px),calc(100%-4px)_100%,4px_100%,0%_calc(100%-4px),0%_4px)]
                    ${isMoreActive ? "bg-[#051173] text-white border-[#C7AB5F]" : "bg-white text-[#051173] border-[#e0dbd2] hover:border-[#051173]"}`}>
                    {isMoreActive ? (dbCategories.find((c) => c.id === activeCatId)?.name ?? "More") : "More"}
                    <ChevronDown size={14} className={`transition-transform ${showMoreDropdown ? "rotate-180" : ""}`} />
                  </button>
                  {showMoreDropdown && (
                    <div className="more-dropdown absolute right-0 top-full mt-2 min-w-[220px] bg-white border border-[#e8e4dd] shadow-xl z-[999] max-h-[60vh] overflow-y-auto">
                      {overflowCats.map((cat) => (
                        <button key={cat.id}
                          onClick={() => { setActiveCatId(cat.id); setVisibleCount(INITIAL_COUNT); setShowMoreDropdown(false); setQuickAddProduct(null); }}
                          className={`flex items-center justify-between w-full text-left px-4 py-3 font-['DM_Mono'] text-[13px] tracking-[0.12em] uppercase border-b last:border-b-0 border-[#f0ebe3] transition-colors
                          ${activeCatId === cat.id ? "bg-[#C7AB5F] text-white" : "text-[#051173] hover:bg-[#f5f5f5]"}`}>
                          <span>{cat.name}</span>
                          {activeCatId === cat.id && <Check size={12} strokeWidth={3} />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {subCats.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar whitespace-nowrap pb-3">
                <span className="font-['DM_Mono'] text-[10px] tracking-[0.18em] uppercase text-[#aaa] mr-1 shrink-0">
                  In {dbCategories.find((c) => c.id === activeCatId)?.name}:
                </span>
                {subCats.map((sub) => (
                  <button key={sub.id}
                    onClick={() => { setActiveCatId(sub.id); setVisibleCount(INITIAL_COUNT); setQuickAddProduct(null); }}
                    className={`shrink-0 font-['DM_Mono'] text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border transition-all duration-200 ${
                      activeCatId === sub.id ? "bg-[#b8955a] text-white border-[#b8955a]" : "bg-white text-[#b8955a] border-[#e0dbd2] hover:border-[#b8955a]"
                    }`}>
                    {sub.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── PRODUCT GRID ── */}
        <section className="max-w-[1160px] mx-auto px-6 md:px-14 py-14 md:py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-['DM_Mono'] text-[13px] tracking-[0.28em] uppercase text-[#1a1a1a] mb-3">
                {activeCatId === null ? "For every woman" : dbCategories.find((c) => c.id === activeCatId)?.name ?? ""}
              </p>
              <div className="w-9 h-px bg-[#b8955a] mb-4" />
              <h2 className="font-['Cormorant_Garamond'] italic font-light text-[clamp(1.8rem,3.5vw,2.8rem)] text-[#1a1a1a]">
                {activeCatName}
              </h2>
            </div>
            <span className="font-['DM_Mono'] text-[13px] text-[#aaa] tracking-wide hidden md:block">
              {loading ? "Loading…" : `${visibleProducts.length} of ${products.length} piece${products.length !== 1 ? "s" : ""}`}
            </span>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader2 size={28} className="text-[#b8955a] animate-spin" />
              <p className="font-['DM_Mono'] text-[13px] tracking-[0.2em] uppercase text-[#aaa]">Loading collection…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <p className="font-['DM_Mono'] text-[13px] text-red-500">{error}</p>
              <button onClick={fetchProducts}
                className="flex items-center gap-2 font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase border border-[#e0dbd2] px-6 py-3 hover:border-[#b8955a] transition-colors">
                <RefreshCw size={13} /> Try again
              </button>
            </div>
          )}

          {!loading && !error && products.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <p className="font-['Cormorant_Garamond'] italic text-[1.5rem] text-[#bbb]">No women's products found</p>
              <p className="font-['DM_Mono'] text-[12px] text-[#aaa] tracking-wide">
                {activeCatId !== null
                  ? `No products in "${dbCategories.find((c) => c.id === activeCatId)?.name}" yet.`
                  : "Add women's products from the admin panel"}
              </p>
              {activeCatId !== null && (
                <button onClick={() => setActiveCatId(null)} className="font-['DM_Mono'] text-[12px] text-[#b8955a] underline underline-offset-2">
                  ← View all women's items
                </button>
              )}
            </div>
          )}

          {!loading && !error && products.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {visibleProducts.map((product) => (
                  <div key={product.id}
                    className="group bg-white border border-[#e8e4dd] overflow-hidden transition-all duration-300 hover:border-[#b8955a] hover:shadow-[0_8px_32px_rgba(0,0,0,0.07)] hover:-translate-y-1 relative">

                    <div className="relative h-[280px] bg-[#f0ebe3] overflow-hidden cursor-pointer"
                      onClick={() => goToProduct(product)}>
                      <img src={primaryImg(product)} alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }} />

                      {product.tag && (
                        <span className={`absolute top-3 left-0 font-['DM_Mono'] text-[12px] tracking-[0.15em] uppercase text-white px-3 py-1.5 rounded-r-sm ${product.tagGold ? "bg-[#b8955a]" : "bg-[#1a1a1a]"}`}>
                          {product.tag}
                        </span>
                      )}

                      <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
                        <button onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
                          className={`p-2 rounded-full shadow-md transition-colors ${wishlist.includes(product.id) ? "bg-[#b8955a] text-white" : "bg-white hover:bg-[#1a1a1a] hover:text-white"}`}>
                          <Heart size={13} fill={wishlist.includes(product.id) ? "currentColor" : "none"} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); goToProduct(product); }}
                          className="p-2 bg-white rounded-full shadow-md hover:bg-[#1a1a1a] hover:text-white transition-colors">
                          <Eye size={13} />
                        </button>
                      </div>

                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out z-20">
                        <button onClick={(e) => { e.stopPropagation(); setQuickAddProduct(quickAddProduct?.id === product.id ? null : product); }}
                          className="bg-[#051173] text-white px-7 pt-5 pb-3.5 rounded-t-full flex flex-col items-center gap-1 hover:bg-[#C7AB5F] transition-colors">
                          <ShoppingBasket size={15} />
                          <span className="font-['DM_Mono'] text-[12px] tracking-widest uppercase whitespace-nowrap">Add to Cart</span>
                        </button>
                      </div>
                    </div>

                    <div className="p-4 cursor-pointer" onClick={() => goToProduct(product)}>
                      <p className="font-['DM_Mono'] text-[11px] tracking-[0.2em] uppercase text-[#b8955a] mb-1">{product.category}</p>
                      <p className="font-['Cormorant_Garamond'] italic text-[1.05rem] text-[#1a1a1a] mb-1.5 line-clamp-2">{product.name}</p>

                      {product.rating_count > 0 && (
                        <div className="text-[#b8955a] text-[13px] tracking-[2px] mb-2">
                          {"★".repeat(Math.round(Number(product.rating_avg)))}{"☆".repeat(5 - Math.round(Number(product.rating_avg)))}
                          <span className="font-['DM_Mono'] text-[11px] text-[#bbb] ml-1">({product.rating_count})</span>
                        </div>
                      )}

                      {product.variants?.colors && product.variants.colors.length > 0 && (
                        <div className="mb-2">
                          <p className="font-['DM_Mono'] text-[9px] tracking-[0.15em] uppercase text-[#bbb] mb-1">Colours</p>
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {product.variants.colors.slice(0, 7).map((c, i) => (
                              <span key={i} title={`${c.value}${c.stock_qty === 0 ? " — Out of stock" : ""}`}
                                className={`w-4 h-4 rounded-full border flex-shrink-0 transition-transform hover:scale-110 ${c.stock_qty === 0 ? "opacity-25 border-[#ddd]" : "border-white ring-1 ring-[#ccc]"}`}
                                style={{ backgroundColor: c.hex }} />
                            ))}
                            {product.variants.colors.length > 7 && <span className="font-['DM_Mono'] text-[9px] text-[#bbb]">+{product.variants.colors.length - 7}</span>}
                          </div>
                        </div>
                      )}

                      {product.variants?.sizes && product.variants.sizes.length > 0 && (
                        <div className="mb-3">
                          <p className="font-['DM_Mono'] text-[9px] tracking-[0.15em] uppercase text-[#bbb] mb-1">Sizes</p>
                          <div className="flex flex-wrap gap-1">
                            {product.variants.sizes.slice(0, 6).map((s) => (
                              <span key={s.value}
                                className={`font-['DM_Mono'] text-[9px] px-1.5 py-0.5 border rounded ${s.stock_qty === 0 ? "border-[#eee] text-[#ccc] line-through" : "border-[#ddd] text-[#666]"}`}>
                                {s.value}
                              </span>
                            ))}
                            {product.variants.sizes.length > 6 && <span className="font-['DM_Mono'] text-[9px] text-[#bbb]">+{product.variants.sizes.length - 6}</span>}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2 border-t border-[#f0ebe3]">
                        <div className="flex items-center gap-1.5">
                          <span className="font-['DM_Mono'] text-[15px] text-[#1a1a1a]">₹{(product.sale_price ?? product.regular_price).toLocaleString()}</span>
                          {product.sale_price && <span className="font-['DM_Mono'] text-[10px] text-[#bbb] line-through">₹{Number(product.regular_price).toLocaleString()}</span>}
                        </div>
                        <span className={`font-['DM_Mono'] text-[10px] px-2 py-0.5 rounded-full ${
                          product.stock === 0 ? "bg-red-50 text-red-400"
                          : product.stock <= 5 ? "bg-amber-50 text-amber-500"
                          : "bg-emerald-50 text-emerald-600"
                        }`}>
                          {product.stock === 0 ? "Out of stock" : product.stock <= 5 ? `Only ${product.stock} left` : "In stock"}
                        </span>
                      </div>
                    </div>

                    {quickAddProduct?.id === product.id && (
                      <QuickAddPopup
                        product={product}
                        onClose={() => setQuickAddProduct(null)}
                        onAddToCart={handleAddToCart}
                        onViewFull={() => { setQuickAddProduct(null); goToProduct(product); }}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center mt-12 gap-3">
                {hasMore ? (
                  <>
                    <button onClick={() => setVisibleCount((v) => v + LOAD_MORE_COUNT)}
                      className="font-['DM_Mono'] text-[14px] tracking-[0.18em] uppercase border-2 border-[#051173] text-[#051173] px-10 py-4 flex items-center gap-3 hover:bg-[#C7AB5F] hover:border-[#C7AB5F] hover:text-white transition-all duration-200">
                      Load More Pieces <ArrowRight size={14} />
                    </button>
                    <p className="font-['DM_Mono'] text-[12px] text-[#aaa] tracking-wide">Showing {visibleProducts.length} of {products.length} pieces</p>
                  </>
                ) : products.length > 0 ? (
                  <p className="font-['DM_Mono'] text-[13px] tracking-[0.2em] uppercase text-[#aaa]">✦ All pieces displayed ✦</p>
                ) : null}
              </div>
            </>
          )}
        </section>

        {/* ── TRUST PILLARS ── */}
        <section className="bg-[#051173] py-10 px-6 md:px-14">
          <div className="max-w-[1160px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <RefreshCw size={32} />, label: "Easy Returns",   sub: "14-day hassle-free policy" },
              { icon: <Shield size={32} />,    label: "Authentic Craft", sub: "Directly from artisans"   },
              { icon: <Star size={32} />,       label: "4.9 / 5 Rating", sub: "12,000+ happy customers"  },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center text-center gap-2">
                <div className="text-[#b8955a]">{item.icon}</div>
                <p className="font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase text-white">{item.label}</p>
                <p className="font-['DM_Mono'] text-[12px] text-white/60 tracking-wide">{item.sub}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── LOOKBOOK ── */}
        <section className="max-w-[1160px] mx-auto px-6 md:px-14 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="font-['DM_Mono'] text-[13px] tracking-[0.28em] uppercase text-[#1a1a1a] mb-3">Editorial</p>
              <div className="w-9 h-px bg-[#b8955a] mb-4" />
              <h2 className="font-['Cormorant_Garamond'] italic font-light text-[clamp(1.8rem,3.5vw,2.8rem)] text-[#1a1a1a]">Shop the Look</h2>
            </div>
            <Link href="/lookbook" className="hidden md:flex items-center gap-2 font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase text-[#888] hover:text-[#1a1a1a] transition-colors">
              View All <ArrowRight size={11} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {LOOKBOOK.map((look) => (
              <div key={look.title} className="group relative overflow-hidden cursor-pointer">
                <div className="h-[420px] overflow-hidden">
                  <img src={look.img} alt={look.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-['DM_Mono'] text-[12px] tracking-[0.2em] uppercase text-white/50 mb-1">{look.subtitle} — {look.count}</p>
                  <p className="font-['Cormorant_Garamond'] italic text-white text-[1.4rem] font-light mb-3">{look.title}</p>
                  <span className="font-['DM_Mono'] text-[13px] tracking-[0.15em] uppercase text-[#c8a96e] flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                    Explore <ArrowRight size={10} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── STYLE TIPS ── */}
        <section className="bg-[#faf7f2] border-t border-b border-[#ede8e0] py-20 px-6 md:px-14">
          <div className="max-w-[1160px] mx-auto">
            <div className="text-center mb-12">
              <p className="font-['DM_Mono'] text-[13px] tracking-[0.28em] uppercase text-[#1a1a1a] mb-3">Style Notes</p>
              <div className="w-9 h-px bg-[#b8955a] mx-auto mb-4" />
              <h2 className="font-['Cormorant_Garamond'] italic font-light text-[clamp(1.8rem,3.5vw,2.8rem)] text-[#1a1a1a]">How to Wear It Well</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {STYLE_TIPS.map((tip) => (
                <div key={tip.title} className="bg-white border border-[#e8e4dd] p-6 hover:border-[#b8955a] transition-colors duration-200">
                  <div className="text-[#b8955a] text-[22px] mb-4">{tip.icon}</div>
                  <p className="font-['Cormorant_Garamond'] italic text-[1.05rem] text-[#1a1a1a] mb-3">{tip.title}</p>
                  <p className="font-['DM_Mono'] text-[12px] leading-[1.8] text-[#555] tracking-wide">{tip.tip}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}